// Updated busesSlice.js with enhanced error handling
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/buses/`;

// Enhanced error handling function
const handleApiError = (error, thunkAPI) => {
  console.log('🔍 Handling Bus API error:', error);
  
  const errorData = error.response?.data;
  const status = error.response?.status;
  
  console.log('Error status:', status);
  console.log('Error data:', errorData);
  console.log('Full error data structure:', JSON.stringify(errorData, null, 2));
  
  let errorMessage = 'حدث خطأ في العملية';
  let fieldErrors = {};
  
  if (status === 404) {
    errorMessage = 'الباص المطلوب غير موجود';
  } else if (status === 403) {
    errorMessage = 'ليس لديك صلاحية لتعديل هذا الباص';
  } else if (status === 401) {
    errorMessage = 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى';
  } else if (errorData) {
    // Handle validation errors with field-specific messages
    if (errorData.name) {
      if (Array.isArray(errorData.name)) {
        fieldErrors.name = errorData.name[0];
        console.log('Name error extracted:', errorData.name[0]);
      } else {
        fieldErrors.name = errorData.name;
      }
    }
    
    if (errorData.bus_number) {
      if (Array.isArray(errorData.bus_number)) {
        fieldErrors.bus_number = errorData.bus_number[0];
        console.log('Bus number error extracted:', errorData.bus_number[0]);
      } else {
        fieldErrors.bus_number = errorData.bus_number;
      }
    }
    
    if (errorData.driver) {
      if (Array.isArray(errorData.driver)) {
        fieldErrors.driver = errorData.driver[0];
        console.log('Driver error extracted:', errorData.driver[0]);
      } else {
        fieldErrors.driver = errorData.driver;
      }
    }
    
    if (errorData.bus_type) {
      if (Array.isArray(errorData.bus_type)) {
        fieldErrors.bus_type = errorData.bus_type[0];
      } else {
        fieldErrors.bus_type = errorData.bus_type;
      }
    }
    
    if (errorData.capacity) {
      if (Array.isArray(errorData.capacity)) {
        fieldErrors.capacity = errorData.capacity[0];
      } else {
        fieldErrors.capacity = errorData.capacity;
      }
    }
    
    if (errorData.manager_name) {
      if (Array.isArray(errorData.manager_name)) {
        fieldErrors.manager_name = errorData.manager_name[0];
      } else {
        fieldErrors.manager_name = errorData.manager_name;
      }
    }
    
    if (errorData.phone_number) {
      if (Array.isArray(errorData.phone_number)) {
        fieldErrors.phone_number = errorData.phone_number[0];
      } else {
        fieldErrors.phone_number = errorData.phone_number;
      }
    }
    
    // Handle general error messages
    if (errorData.detail) {
      errorMessage = errorData.detail;
    } else if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
      errorMessage = errorData.non_field_errors[0];
    } else if (typeof errorData === 'string') {
      errorMessage = errorData;
    } else if (Object.keys(fieldErrors).length > 0) {
      errorMessage = 'يرجى تصحيح الأخطاء في الحقول المحددة';
    }
  }
  
  const result = {
    message: errorMessage,
    fieldErrors,
    originalError: errorData,
    status
  };
  
  console.log('🔍 Bus Error handling result:', result);
  console.log('Field errors extracted:', fieldErrors);
  
  return thunkAPI.rejectWithValue(result);
};

export const fetchBuses = createAsyncThunk(
  'buses/fetchBuses', 
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(BASE_URL);
      return response.data.map(bus => ({
        id: bus.id,
        plateNumber: bus.bus_number || '',
        route: bus.bus_type || '',
        driverName: bus.driver_name || 'غير محدد',
        ...bus,
      }));
    } catch (error) {
      return handleApiError(error, thunkAPI);
    }
  }
);

export const createBus = createAsyncThunk(
  'buses/createBus', 
  async (bus, thunkAPI) => {
    try {
      console.log('🔄 API Call - Creating bus:', bus);
      const response = await axios.post(BASE_URL, bus);
      console.log('✅ Create Bus API Response:', response.data);
      return {
        id: response.data.id,
        plateNumber: response.data.bus_number || '',
        route: response.data.bus_type || '',
        driverName: response.data.driver_name || 'غير محدد',
        ...response.data,
      };
    } catch (error) {
      console.error('❌ Create Bus API Error:', error);
      return handleApiError(error, thunkAPI);
    }
  }
);

export const updateBus = createAsyncThunk(
  'buses/updateBus', 
  async ({ id, data }, thunkAPI) => {
    try {
      console.log('🔄 API Call - Updating bus:', { id, data });
      const response = await axios.put(`${BASE_URL}${id}/`, data);
      console.log('✅ Update Bus API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Update Bus API Error:', error);
      return handleApiError(error, thunkAPI);
    }
  }
);

export const deleteBus = createAsyncThunk(
  'buses/deleteBus',
  async (id, thunkAPI) => {
    try {
      console.log('🔄 API Call - Deleting bus:', id);
      await axios.delete(`${BASE_URL}${id}/`);
      console.log('✅ Delete Bus successful');
      return id;
    } catch (error) {
      console.error('❌ Delete Bus API Error:', error);
      return handleApiError(error, thunkAPI);
    }
  }
);

export const fetchBusById = createAsyncThunk(
  'buses/fetchBusById',
  async (id, thunkAPI) => {
    try {
      console.log('🔄 API Call - Fetching bus by ID:', id);
      const response = await axios.get(`${BASE_URL}${id}/`);
      console.log('✅ Fetch Bus by ID API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Fetch Bus by ID API Error:', error);
      return handleApiError(error, thunkAPI);
    }
  }
);

export const assignStudentsToBus = createAsyncThunk(
  'buses/assignStudentsToBus',
  async ({ busId, studentIds, action }, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}${busId}/assign-students/`, {
        student_ids: studentIds,
        action,
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, thunkAPI);
    }
  }
);

const busesSlice = createSlice({
  name: 'buses',
  initialState: {
    list: [],
    filtered: [],
    filters: {
      searchQuery: '',
      status: '',
      type: '',
    },
    selectedBus: null,
    selectedBusStatus: 'idle',
    selectedBusError: null,
    status: 'idle',
    error: null,
    
    // Operation-specific errors
    deleteError: null,
    createError: null,
    updateError: null,
    
    // Field-specific validation errors
    fieldErrors: {},
    
    // Loading states for specific operations
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    assignStudentsStatus: 'idle',
    assignStudentsError: null,
  },
  reducers: {
    setSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload;
      busesSlice.caseReducers.applyFilters(state);
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      busesSlice.caseReducers.applyFilters(state);
    },
    setTypeFilter: (state, action) => {
      state.filters.type = action.payload;
      busesSlice.caseReducers.applyFilters(state);
    },
    
    // Error clearing actions
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    clearCreateError: (state) => {
      state.createError = null;
      state.fieldErrors = {};
    },
    clearUpdateError: (state) => {
      state.updateError = null;
      state.fieldErrors = {};
    },
    clearAllErrors: (state) => {
      state.deleteError = null;
      state.createError = null;
      state.updateError = null;
      state.fieldErrors = {};
      state.error = null;
      state.selectedBusError = null;
    },
    
    applyFilters: (state) => {
      const { searchQuery, status, type } = state.filters;
      let filtered = [...state.list];

      if (searchQuery) {
        filtered = filtered.filter((b) =>
          b.plateNumber.includes(searchQuery) ||
          b.driverName.includes(searchQuery) ||
          b.route.includes(searchQuery)
        );
      }

      if (status) {
        filtered = filtered.filter((b) => b.status === status);
      }

      if (type) {
        filtered = filtered.filter((b) => b.type === type);
      }

      state.filtered = filtered;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Buses
      .addCase(fetchBuses.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBuses.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload;
        state.filtered = action.payload;
        state.error = null;
      })
      .addCase(fetchBuses.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || action.error.message;
        console.log('Fetch buses failed:', action.payload);
      })
      
      // Create Bus
      .addCase(createBus.pending, (state) => {
        state.isCreating = true;
        state.createError = null;
        state.fieldErrors = {};
      })
      .addCase(createBus.fulfilled, (state, action) => {
        state.isCreating = false;
        state.list.push(action.payload);
        state.filtered.push(action.payload);
        state.createError = null;
        state.fieldErrors = {};
      })
      .addCase(createBus.rejected, (state, action) => {
        state.isCreating = false;
        console.log('Create bus rejected with payload:', action.payload);
        state.createError = action.payload;
        state.fieldErrors = action.payload?.fieldErrors || {};
      })
      
      // Update Bus
      .addCase(updateBus.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
        state.fieldErrors = {};
      })
      .addCase(updateBus.fulfilled, (state, action) => {
        state.isUpdating = false;
        const updated = action.payload;
        const index = state.list.findIndex(b => b.id === updated.id);
        
        if (index !== -1) {
          state.list[index] = {
            ...state.list[index],
            ...updated,
            plateNumber: updated.bus_number || '',
            route: updated.bus_type || '',
            driverName: updated.driver_name || 'غير محدد',
          };
        }
        
        // Update filtered list
        const filteredIndex = state.filtered.findIndex(b => b.id === updated.id);
        if (filteredIndex !== -1) {
          state.filtered[filteredIndex] = {
            ...state.filtered[filteredIndex],
            ...updated,
            plateNumber: updated.bus_number || '',
            route: updated.bus_type || '',
            driverName: updated.driver_name || 'غير محدد',
          };
        }
        
        state.updateError = null;
        state.fieldErrors = {};
      })
      .addCase(updateBus.rejected, (state, action) => {
        state.isUpdating = false;
        console.log('Update bus rejected with payload:', action.payload);
        state.updateError = action.payload;
        state.fieldErrors = action.payload?.fieldErrors || {};
      })
      
      // Delete Bus
      .addCase(deleteBus.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
      })
      .addCase(deleteBus.fulfilled, (state, action) => {
        state.isDeleting = false;
        const deletedId = action.payload;
        state.list = state.list.filter((bus) => bus.id !== deletedId);
        state.filtered = state.filtered.filter((bus) => bus.id !== deletedId);
        state.deleteError = null;
      })
      .addCase(deleteBus.rejected, (state, action) => {
        state.isDeleting = false;
        console.log('Delete bus rejected with payload:', action.payload);
        state.deleteError = action.payload;
      })
      
      // Fetch Bus By ID
      .addCase(fetchBusById.pending, (state) => {
        state.selectedBusStatus = 'loading';
        state.selectedBusError = null;
      })
      .addCase(fetchBusById.fulfilled, (state, action) => {
        state.selectedBus = action.payload;
        state.selectedBusStatus = 'succeeded';
        state.selectedBusError = null;
      })
      .addCase(fetchBusById.rejected, (state, action) => {
        state.selectedBusError = action.payload?.message || action.error.message;
        state.selectedBusStatus = 'failed';
        console.log('Fetch bus by ID failed:', action.payload);
      })

      // Assign Students To Bus
      .addCase(assignStudentsToBus.pending, (state) => {
        state.assignStudentsStatus = 'loading';
        state.assignStudentsError = null;
      })
      .addCase(assignStudentsToBus.fulfilled, (state, action) => {
        state.assignStudentsStatus = 'succeeded';
        state.selectedBus = action.payload;
      })
      .addCase(assignStudentsToBus.rejected, (state, action) => {
        state.assignStudentsStatus = 'failed';
        state.assignStudentsError = action.payload?.message || action.error.message;
      });
  },
});

export const {
  setSearchQuery,
  setStatusFilter,
  setTypeFilter,
  applyFilters,
  clearDeleteError,
  clearCreateError,
  clearUpdateError,
  clearAllErrors,
} = busesSlice.actions;

// Selectors
export const selectBuses = (state) => state.buses.list;
export const selectFilteredBuses = (state) => state.buses.filtered;
export const selectBusesStatus = (state) => state.buses.status;
export const selectBusesError = (state) => state.buses.error;
export const selectSelectedBus = (state) => state.buses.selectedBus;
export const selectCreateBusError = (state) => state.buses.createError;
export const selectUpdateBusError = (state) => state.buses.updateError;
export const selectDeleteBusError = (state) => state.buses.deleteError;
export const selectBusFieldErrors = (state) => state.buses.fieldErrors;
export const selectIsCreatingBus = (state) => state.buses.isCreating;
export const selectIsUpdatingBus = (state) => state.buses.isUpdating;
export const selectIsDeletingBus = (state) => state.buses.isDeleting;
export const selectAssignStudentsStatus = (state) => state.buses.assignStudentsStatus;
export const selectAssignStudentsError = (state) => state.buses.assignStudentsError;

export default busesSlice.reducer;